import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { GROUP_LOGOS, groupNameError, normalizeGroupName } from './candidateGroupUtils';

export default function CandidateGroupFormModal({
  open,
  group,
  saving,
  companies,
  companyId,
  companyRequired,
  onCompanyChange,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logo, setLogo] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(group?.name || '');
    setDescription(group?.description || '');
    setLogo(group?.logo || '');
    setNameError('');
  }, [open, group]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const error = groupNameError(name);
    setNameError(error);
    if (error) return;
    if (companyRequired && !companyId) {
      setNameError(t('candidateGroups.companyRequired', 'Select a company before creating a group.'));
      return;
    }
    onSave({
      name: normalizeGroupName(name),
      description: description.trim(),
      logo,
    });
  };

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-lg rounded-xl bg-white shadow-xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 className="text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>
            {group
              ? t('candidateGroups.editTitle', 'Edit Candidate Group')
              : t('candidateGroups.createTitle', 'Create Candidate Group')}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3 px-5 py-3 text-[10.5px]">
          {companyRequired && (
            <label className="block text-[10.5px]" style={{ fontSize: '10.5px' }}>
              <span className="mb-1 block font-medium text-gray-700 text-[10.5px]">{t('candidateGroups.company', 'Company')}</span>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[10.5px]"
                style={{ fontSize: '10.5px' }}
                value={companyId || ''}
                onChange={(event) => onCompanyChange(event.target.value)}
              >
                <option value="" style={{ fontSize: '10.5px' }}>{t('candidateGroups.selectCompany', 'Select a company')}</option>
                {(companies || []).map((company) => (
                  <option key={company.id} value={company.id} style={{ fontSize: '10.5px' }}>{company.name}</option>
                ))}
              </select>
            </label>
          )}
          <div className="text-[10.5px]" style={{ fontSize: '10.5px' }}>
            <span className="mb-1 block font-medium text-gray-700 text-[10.5px]">{t('candidateGroups.logo', 'Logo')}</span>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setLogo('')}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border text-[10.5px] ${logo ? 'border-gray-200 text-gray-400' : 'border-blue-500 bg-blue-50 text-blue-700'}`}
                aria-label={t('candidateGroups.noLogo', 'No logo')}
                style={{ fontSize: '10.5px' }}
              >
                —
              </button>
              {GROUP_LOGOS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setLogo(emoji)}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border text-[10.5px] ${logo === emoji ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  aria-label={emoji}
                  aria-pressed={logo === emoji}
                  style={{ fontSize: '10.5px' }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          <label className="block text-[10.5px]" style={{ fontSize: '10.5px' }}>
            <span className="mb-1 block font-medium text-gray-700 text-[10.5px]">{t('candidateGroups.name', 'Group Name')}</span>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[10.5px]"
              style={{ fontSize: '10.5px' }}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setNameError('');
              }}
              maxLength={160}
              autoFocus
            />
            {nameError && <p className="mt-1 text-[10.5px] text-red-600">{nameError}</p>}
          </label>
          <label className="block text-[10.5px]" style={{ fontSize: '10.5px' }}>
            <span className="mb-1 block font-medium text-gray-700 text-[10.5px]">{t('candidateGroups.description', 'Description')}</span>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[10.5px]"
              style={{ fontSize: '10.5px' }}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={1000}
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-1.5 text-[10.5px] text-gray-700 hover:bg-gray-100" style={{ fontSize: '10.5px' }}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-[10.5px] font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            style={{ fontSize: '10.5px' }}
          >
            {saving ? t('candidateGroups.saving', 'Saving...') : t('candidateGroups.save', 'Save')}
          </button>
        </div>
      </form>
    </div>
  );
}
